import { X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { useEmailAccountDetail } from '@/hooks/useDispatcher';
import { formatDateTime } from '@/lib/utils';

interface Props {
  accountId: string | null;
  workspaceId: string;
  onClose: () => void;
}

export function EmailAccountDetailDrawer({ accountId, workspaceId, onClose }: Props) {
  const { data: account, isLoading } = useEmailAccountDetail(accountId, workspaceId);

  if (!accountId) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-20 bg-black/20" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 z-30 h-full w-96 bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-brand-border">
          <h2 className="font-semibold text-brand-dark text-sm">Détail du compte</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : !account ? (
            <p className="text-sm text-gray-400">Compte introuvable.</p>
          ) : (
            <>
              <Row label="Email" value={<span className="font-mono text-xs">{account.email}</span>} />
              <Row label="Provider" value={account.provider} />
              <Row
                label="Statut"
                value={<Badge tone={account.status === 'active' ? 'green' : account.status === 'warmup' ? 'yellow' : 'gray'}>{account.status}</Badge>}
              />
              <Row label="Limite quotidienne" value={`${account.daily_limit}`} />
              <Row label="Envoyé aujourd'hui" value={`${account.current_daily_sent}`} />
              {account.warmup_progress !== null && (
                <Row label="Warmup" value={`${account.warmup_progress}%`} />
              )}
              {account.domain && (
                <Row label="Domaine" value={account.domain.domain} />
              )}

              {account.groups.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Groupes</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {account.groups.map((g) => (
                      <span
                        key={g.id}
                        className="text-xs px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: g.color ?? '#3b82f6' }}
                      >
                        {g.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {account.assignment && (
                <Row
                  label="Campagne assignée"
                  value={
                    <span className="text-xs font-mono text-blue-600">
                      {account.assignment.campaign_id}
                    </span>
                  }
                />
              )}

              <Row label="Créé" value={formatDateTime(account.created_at)} />
              <Row label="Modifié" value={formatDateTime(account.updated_at)} />
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-brand-dark">{value}</span>
    </div>
  );
}
